import { useQuery } from '@apollo/client';
import {
  EEntityOwnership,
  EFilterType,
  TEntityOwnership,
  TFilter,
  TFilterEvent,
} from 'components/Filter/filter.types';
import { useDialog, useLogEvent } from 'components/hooks';
import { ACTIVITIES_TABLE_ROWS_PER_PAGE } from 'constants/pagination';
import { GET_ACTIVITIES } from 'graphql/secure/queries/activity.queries';
import { GET_BASE_PARTNER_COMPANY } from 'graphql/secure/queries/partner.queries';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IActivity } from 'types/activity.types';
import { IBasePartnerCompany } from 'types/partner.types';
import { GraphQlConnection } from 'types/types';
import { createEnumParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';

interface IUseActivitiesProps {
  // Can be used to skip the useQueryParam search and use the internal search term instead
  skipUseQueryParams?: boolean;

  defaultOwnedById?: string;
}

interface IQueryState {
  searchTerm?: string | null;
  owner?: TEntityOwnership | null;
  ownedById?: string | null;
}

const useActivities = ({ defaultOwnedById, skipUseQueryParams }: IUseActivitiesProps = {}) => {
  const { openDialog } = useDialog();
  const { logEvent } = useLogEvent();
  const navigate = useNavigate();
  const { id: partnerLibraryId } = useParams<'id'>();

  const [query, setQuery] = useQueryParams(
    {
      searchTerm: withDefault(StringParam, null),
      owner: withDefault(
        createEnumParam<TEntityOwnership>(Object.values(EEntityOwnership)),
        defaultOwnedById ? EEntityOwnership.PARTNER : null
      ),
      ownedById: withDefault(StringParam, defaultOwnedById),
    },
    { removeDefaultsFromUrl: true }
  );

  const [internalQueryState, setInternalQueryState] = useState<IQueryState>({
    searchTerm: '',
    owner: defaultOwnedById ? EEntityOwnership.PARTNER : null,
    ownedById: defaultOwnedById,
  });

  const { searchTerm, owner, ownedById } = skipUseQueryParams ? internalQueryState : query;

  const filters: TFilter[] = useMemo(
    () => [
      {
        type: EFilterType.OWNERSHIP,
        value: owner
          ? {
              option: owner,
              ownedById,
            }
          : undefined,
      },
    ],
    [owner, ownedById]
  );

  const { data, loading, error, fetchMore } = useQuery<{
    activities: GraphQlConnection<IActivity>;
  }>(GET_ACTIVITIES, {
    // when using this inside the partner activity page, we need to pass the companyId to get the activities of that partner
    variables: {
      filters: {
        ...(searchTerm ? { searchTerm } : undefined),
        ...((partnerLibraryId || (owner === EEntityOwnership.PARTNER && ownedById)) && {
          ownedById: partnerLibraryId || ownedById,
        }),
        ...(owner && owner !== EEntityOwnership.PARTNER && { ownershipType: owner }),
      },
      first: ACTIVITIES_TABLE_ROWS_PER_PAGE,
    },
  });

  const { data: partnerData } = useQuery<{ partner: IBasePartnerCompany }>(
    GET_BASE_PARTNER_COMPANY,
    {
      skip: !partnerLibraryId,
      variables: {
        id: partnerLibraryId,
      },
    }
  );

  const hasNextPage: boolean = !!data?.activities?.pageInfo?.hasNextPage;
  const endCursor: string | undefined = data?.activities?.pageInfo?.endCursor;

  const activities = useMemo(
    () => (data?.activities ? removeGraphConnections(data.activities) : []),
    [data?.activities]
  );

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const handleCreateActivity = () =>
    openDialog({
      type: 'ADD_EDIT_ACTIVITY',
      props: {
        mode: 'add-activity',
        partner: partnerLibraryId ? partnerData?.partner : undefined,
        hidePartnerSelector: !!partnerLibraryId,
      },
    });

  const handleEditActivity = (activity: IActivity) => {
    openDialog({
      type: 'ADD_EDIT_ACTIVITY',
      props: {
        mode: 'edit-activity',
        activity,
        hidePartnerSelector: !!partnerLibraryId,
      },
    });
  };

  const handleSearchTermChange = (searchTerm: string) => {
    logEvent('ACTIVITY_SEARCHED', { searchTerm });
    skipUseQueryParams
      ? setInternalQueryState(prev => ({ ...prev, searchTerm }))
      : setQuery(prev => ({ ...prev, searchTerm }));
  };

  const handleFilterChange = (event: TFilterEvent) => {
    if (event.type === EFilterType.OWNERSHIP) {
      skipUseQueryParams
        ? setInternalQueryState(prev => ({
            ...prev,
            owner: event.value?.option || undefined,
            ownedById: event.value?.ownedById || undefined,
          }))
        : setQuery(prev => ({
            ...prev,
            owner: event.value?.option || undefined,
            ownedById: event.value?.ownedById || undefined,
          }));
    }
  };

  const handleFilterReset = () => {
    skipUseQueryParams
      ? setInternalQueryState(prev => ({
          ...prev,
          owner: undefined,
          ownedById: undefined,
        }))
      : setQuery(prev => ({
          owner: undefined,
          ownedById: undefined,
        }));
  };

  const handleSelectActivity = (activity: IActivity) => navigate(`/activities/${activity.id}`);

  return {
    hasNextPage,
    activities,
    loading,
    error,
    searchTerm,
    filters,
    partner: partnerData?.partner,
    handleSearchTermChange,
    handleCreateActivity,
    handleEditActivity,
    handleFilterChange,
    handleFilterReset,
    handlePageEndReached,
    handleSelectActivity,
  };
};

export default useActivities;
